import {filter, find, forEach, isBoolean} from 'lodash';
import promiseRetry from 'promise-retry';
import {useEffect, useCallback, useState, useMemo, useRef} from 'react';
import {useBeforeunload} from 'react-beforeunload';
import {useTranslation} from 'react-i18next';
import {useSocketEvent} from 'socket.io-react-hook';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {clientActionsApi} from 'api';
import {PictureInPictureObserver, notifier} from 'components';
import {envConfig} from 'config';
import {NOTIFY_EVENT_NAMES} from 'constants/client';
import {REMOTE_MEDIA_ID, SCREENSHOT_CANVAS_ID, SystemEndSessionCode} from 'constants/session';
import {ClientContext} from 'context';
import {
    PartialSessionChangeName, SocketEvent, SocketManagerEvent, VideoPinParticipant, VoiceInputMode, WebRTCServerSource
} from 'enums';
import {
    useAudioStreamRecord, useClientSession, useSocketEventCallback, usePrevious, useIsChanged, useIsChangedTo,
    useSpeechDetection, useTwilioDevice, useVoiceReplyPlayback
} from 'hooks';
import {
    AsyncRequestData, AsyncRequestStatusName, AsyncRequestTypeName, ClientDevice, ClientParticipant, ParticipantAsyncRequest,
    ScreenSharingActivityStatus, SessionActivityTextChatData, SessionActivityTextChatDataState, SessionFeedback,
    SessionResult, SessionResultStatus, SessionScreenshotSubject, SessionStatus, SessionActivityAudioVideoData, Session
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {DevicePermission} from 'pages/User/Conference/types';
import {InviteeToken, pictureInPictureStore, inSessionDataStore, storage} from 'services';
import {
    PartialSessionChange, NotifyClientResult, TextChatThreads, WebRTCConnectDetails,
    RequestAppointmentDetails, SessionActivityTextChatInteractiveChatState, TextChatMessage,
    ParticipantRole, DefaultParticipant
} from 'types';
import {apiUtils, getScreenshotBase64, logger, mediaDevicesUtils, sessionUtils} from 'utils';

import {ClientNotifier} from './ClientNotifier';
import {ClientSessionResponsePayload, SessionActionsContainerContextProps} from './types';

const {isDevelopmentMode} = envConfig;
// Longest single push-to-talk turn. Generous for one spoken message, but bounds a stuck button.
const MAX_VOICE_INPUT_DURATION = 60000;
// Keep recording briefly after the button is released. Speakers trail off past the release, and the speech
// service needs a moment of trailing silence to finalise the last phrase — cutting the audio at the exact
// moment of release is what clipped the end of it. Kept short: the button stays lit for this whole window.
const VOICE_INPUT_RELEASE_DELAY = 500;
// Release the microphone track after this long without recording. The track is kept between turns so
// getUserMedia latency does not eat the first words, but holding it open indefinitely leaves the browser's
// recording indicator lit — so a quiet client gets the track released and re-acquired on the next press.
const VOICE_INPUT_IDLE_RELEASE = 45000;
// Hands-free keeps listening while the agent's reply plays, so the browser has to cancel what the speakers emit
// — without it the bot's own voice trips speech detection and interrupts itself.
const VOICE_INPUT_CONSTRAINTS: MediaStreamConstraints = {
    audio: {echoCancellation: true, noiseSuppression: true, autoGainControl: true}
};
const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.IN_SESSION_VIEW.NOTIFICATIONS;
const GEOLOCATION_ERROR_T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.GEOLOCATION_ERROR;

enum GEOLOCATION_ERROR_CODE {
    NotSupported = -1,
    NotAllowed = 1
}

type Props = {
    socket: SocketLikeWithNamespace;
    payload: ClientSessionResponsePayload;
    contextProps: SessionActionsContainerContextProps;
    device: ClientDevice;
    clientNotifier: ClientNotifier;
    errorMessage?: string;
    children: React.ReactElement;
    localSessionEnded: boolean;
    setLocalSessionEnded: (value: boolean) => void;
    startReassignedSession: () => void;
    handleNotificationResult: (result: NotifyClientResult, token?: string) => Promise<void>;
}

export const ClientSessionActions: React.FC<Props> = ({
    socket, payload, contextProps, device, clientNotifier, errorMessage, children, localSessionEnded,
    setLocalSessionEnded, startReassignedSession, handleNotificationResult
}: Props) => {
    const {t} = useTranslation();
    const {
        session, agent, token, screenSharingActivity, localParticipant, sessionParams, sessionVersion, notifyStatuses,
        webRTCActivities, textChatActivity, avatarActivity, sessionStatus, isCurrentStatus, isPreviousStatus, audioVideoActivity,
        audioActivity, sipActivity, fullScreenVideoDefault, isWebviewVideoOnly, workingHours, videoPinnedParticipant,
        audioVideoViewConference, isClient, audioStreamEnabled, transcriptionLanguage
    } = useClientSession({payload});
    const preSessionFormDrafts = useRef<Record<string, {
        values: Record<string, string>;
        initialValues: Record<string, string>;
    }>>({});
    useEffect(() => {
        preSessionFormDrafts.current = {};
    }, [session?.id]);
    const prevTextChatActivity = usePrevious(textChatActivity);
    const prevAvatarActivity = usePrevious(avatarActivity);
    const isPayloadChanged = useIsChanged(payload);
    const isStateChangedToFinished = useIsChangedTo(localSessionEnded, true);
    const prevSessionVersion = usePrevious(sessionVersion);
    const prevSession = usePrevious(session);
    const {currentView} = useMemo(() => contextProps, [contextProps]);
    const [textChatThreads, setTextChatThreads] = useState<TextChatThreads>();
    const [avatarThreads, setAvatarThreads] = useState<TextChatThreads>();
    const [textChatInteractiveChatState, setTextChatInteractiveChatState] = useState<SessionActivityTextChatInteractiveChatState>(null);
    const [socketId, setSocketId] = useState<string>();
    const [tracks, setTracks] = useState<MediaStreamTrack[]>([]);
    const [voiceTracks, setVoiceTracks] = useState<MediaStreamTrack[]>([]);
    const [voiceRecording, setVoiceRecording] = useState(false);
    // Hands-free: the microphone is switched on once and stays on; speech detection decides where each phrase
    // starts and ends. `voiceRecording` then means "a phrase is being captured", not "the button is held".
    const [voiceArmed, setVoiceArmed] = useState(false);
    // Chatbot is ending the session: freeze text input until the session actually finishes.
    const [conversationEnding, setConversationEnding] = useState(false);
    // Mirror of voiceTracks so timers/session-end can stop the track without re-running on every tracks change.
    const voiceTracksRef = useRef<MediaStreamTrack[]>([]);
    const voiceInputTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const voiceInputReleaseRef = useRef<ReturnType<typeof setTimeout>>();
    const voiceInputIdleRef = useRef<ReturnType<typeof setTimeout>>();
    // The first mic-on is the gesture that unlocks audio playback, so it is the one moment we can replay the
    // last bot phrase (the greeting). Only once — re-arming later must not repeat it.
    const voiceReplayRequestedRef = useRef(false);
    const [untreatedAsyncRequests, setUntreatedAsyncRequests] = useState<ParticipantAsyncRequest[]>([]);
    const {device: twilioDevice, setDevice: setTwilioDevice, init: initTwilioDevice} = useTwilioDevice(ParticipantRole.Client);

    const devicePermission = useMemo(() => {
        const activity = audioVideoActivity || audioActivity;
        const data = activity?.data as SessionActivityAudioVideoData;

        const participantDevicePermission = data?.participants?.[localParticipant?.id]?.devicePermission;
        if (!participantDevicePermission) {
            return null;
        }

        return participantDevicePermission;
    }, [audioVideoActivity, audioActivity, localParticipant]);
    const audioEnabled = useMemo(() => !devicePermission?.audio?.disabled, [devicePermission]);
    const videoEnabled = useMemo(() => !devicePermission?.video?.disabled, [devicePermission]);
    const audioEnabledChanged = useIsChanged(audioEnabled, audioEnabled);
    const videoEnabledChanged = useIsChanged(videoEnabled, videoEnabled);
    const changedBy = useMemo(() => devicePermission?.changedBy, [devicePermission]);

    const [fullScreenVideo, setFullScreenVideo] = useState(fullScreenVideoDefault);
    const [participantsNetworkQuality, setParticipantsNetworkQuality] = useState<{[participantId: string]: number}>({});
    const [videoConstraints, setVideoConstraints] = useState<MediaTrackConstraintSet>(mediaDevicesUtils.getVideoTrackConstraintSet());
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [videoPinParticipant, setVideoPinParticipant] = useState<VideoPinParticipant>(videoPinnedParticipant);
    const [pinnedParticipantId, setPinnedParticipantId] = useState<string>();

    const {sendMessage: emitReauthenticate} = useSocketEvent(socket, SocketEvent.Reauthenticate);

    const setMediaDevices = useCallback((devices: MediaDeviceInfo[]) => {
        setVideoDevices(mediaDevicesUtils.filterCameras(devices));
        setAudioDevices(mediaDevicesUtils.filterMicrophones(devices));
    }, []);

    const updateDevicePermission = useCallback((type: DevicePermission, disabled: boolean) => {
        const activity = audioVideoActivity || audioActivity;
        clientActionsApi.addActivityData(sessionParams, activity.id, {devicePermission: {[type]: {disabled}}});
    }, [sessionParams, audioVideoActivity, audioActivity]);

    const onToggleAudio = useCallback(() => {
        updateDevicePermission(DevicePermission.Audio, audioEnabled);
    }, [audioEnabled, updateDevicePermission]);

    const onToggleVideo = useCallback(() => {
        updateDevicePermission(DevicePermission.Video, videoEnabled);
    }, [videoEnabled, updateDevicePermission]);

    const onToggleFullScreenVideo = useCallback((value?: boolean) => {
        if (isBoolean(value)) {
            setFullScreenVideo(value);
        } else {
            setFullScreenVideo((prev) => !prev);
        }
    }, []);

    const onLocalNetworkQualityLevelChange = useCallback((level: number) => {
        setParticipantsNetworkQuality((prev) => ({...prev, [localParticipant.id]: level}));
    }, [localParticipant]);

    const onRemoteNetworkQualityLevelChange = useCallback((level: number) => {
        if (agent) {
            setParticipantsNetworkQuality((prev) => ({...prev, [agent.id]: level}));
        }
    }, [agent]);

    const notifyWebRTCActivities =
    useCallback((source: WebRTCServerSource, status: 'success' | 'error', details?: WebRTCConnectDetails) => {
        sessionUtils.filterWebRTCActivitiesBySource(webRTCActivities, source).forEach((activity) => {
            const data = {
                connection: {
                    ...(details || {}),
                    status
                }
            };
            clientActionsApi.addActivityData(sessionParams, activity.id, data);
        });
    }, [webRTCActivities, sessionParams]);

    const notifyWebRTCStartSuccess = useCallback((source: WebRTCServerSource, connectStatus?: WebRTCConnectDetails) => {
        notifyWebRTCActivities(source, 'success', connectStatus);
    }, [notifyWebRTCActivities]);

    const notifyWebRTCStartFailed = useCallback((source: WebRTCServerSource, error: WebRTCConnectDetails) => {
        notifyWebRTCActivities(source, 'error', error);
    }, [notifyWebRTCActivities]);

    const updateParticipantAsyncRequest = useCallback((requestType: AsyncRequestTypeName, status: AsyncRequestStatusName, data: any = {}) =>
        promiseRetry((retry) =>
            clientActionsApi.updateParticipantAsyncRequest(sessionParams, {
                requestType,
                status,
                ...(data && data)
            }).catch(retry),
        {retries: 3, minTimeout: 2000}), [sessionParams]);

    const setTreatedAsyncRequest = useCallback((type: AsyncRequestTypeName) => {
        setUntreatedAsyncRequests((prev) => filter(prev, (request) => request.type !== type));
    }, []);

    const statusChanged = useCallback(({status: newStatus, result}: Session<ClientParticipant>) => {
        if (prevSession && newStatus === SessionStatus.InSession
                && !isPreviousStatus(SessionStatus.InSession) && !isPreviousStatus(SessionStatus.OnHold)) {
            handleNotificationResult(clientNotifier.sessionStarted());
        } else if (newStatus === SessionStatus.SessionEnded && !isPreviousStatus(SessionStatus.SessionEnded) &&
            result.information === SystemEndSessionCode.AfterHours) {
            const dateMs = new Date(workingHours.nextAvailableDate).getTime();
            handleNotificationResult(clientNotifier.afterHours(dateMs));
        }
        const notifyStatus = find(notifyStatuses, (notify) => notify.status === newStatus);
        if (notifyStatus) {
            clientNotifier.sessionStatusChanged(notifyStatus.data);
        }
    }, [handleNotificationResult, isPreviousStatus, clientNotifier, notifyStatuses, prevSession, workingHours]);

    const resetSessionValues = useCallback(() => {
        setTextChatThreads(null);
        setAvatarThreads(null);
    }, []);

    const notifyAboutEndSession = useCallback(async (result?: Partial<SessionResult>, _token?: string) => {
        if (result?.status === SessionResultStatus.Success) {
            await handleNotificationResult(clientNotifier.sessionSuccess(result.information));
        } else if (result?.status && result?.information) {
            await handleNotificationResult(clientNotifier.sessionFailed(result.information));
        } else {
            await handleNotificationResult(clientNotifier.sessionFailed());
        }
        void handleNotificationResult(clientNotifier.sessionClosed(), _token);
    }, [clientNotifier, handleNotificationResult]);

    const setSessionEndedState = useCallback(() => {
        setLocalSessionEnded(true);
    }, [setLocalSessionEnded]);

    useSocketEventCallback(socket, SocketEvent.Authenticated, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.Authenticated}`);
        setSocketId(socket.id);
    }, [socket.id]));

    useSocketEventCallback(socket, SocketManagerEvent.ReconnectFailed, useCallback(() => {
        logger.debug(`Received socket event: ${SocketManagerEvent.ReconnectFailed}`);
        setSessionEndedState();
    }, [setSessionEndedState]));

    useSocketEventCallback(socket, SocketEvent.ActiveSessionPartiallyChanged, useCallback((change: PartialSessionChange) => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionPartiallyChanged}`, change);
        if (change.name === PartialSessionChangeName.TextChat && change.data) {
            setTextChatThreads((prev) => {
                const oldMessages = prev?.[change.data.threadId] || [];
                const newMessages = [...oldMessages, change.data.message];
                const newThread = {...(prev || {}), [change.data.threadId]: newMessages};
                return newThread;
            });
        }
        if (change.name === PartialSessionChangeName.Avatar && change.data) {
            setAvatarThreads((prev) => {
                const oldMessages = prev?.[change.data.threadId] || [];
                const newMessages = [...oldMessages, change.data.message];
                const newThread = {...(prev || {}), [change.data.threadId]: newMessages};
                return newThread;
            });
        }
    }, []));

    // socketId only catches up on the Authenticated event, so between a reconnect and that event it still holds the
    // previous id while the session already carries the new one — on that mismatch alone the tab would mistake itself
    // for a second tab and end its own session. The live socket.id settles it: if the session points at this tab's
    // current socket, this is the tab that owns the session, however stale the state is.
    const isOpenedInNewTab = useCallback((_session: {localParticipant: ClientParticipant} = {localParticipant}) => {
        const participantSocketId = _session?.localParticipant?.socket?.id;
        return participantSocketId && socketId
            && participantSocketId !== socketId && participantSocketId !== socket.id;
    }, [localParticipant, socketId, socket.id]);

    const refreshAuth = useCallback(() => {
        logger.debug('Refresh auth with token: ', token);
        if (token) {
            emitReauthenticate(token);
        }
    }, [token, emitReauthenticate]);

    const isSessionEnded = useCallback((_session) => !!_session?.endDate, []);

    const setScreenSharingActivityStatus = useCallback((status: ScreenSharingActivityStatus) => {
        if (!screenSharingActivity) {
            return null;
        }
        const data = {
            ...screenSharingActivity.data,
            status
        };
        return clientActionsApi.addActivityData(sessionParams, screenSharingActivity.id, data);
    }, [screenSharingActivity, sessionParams]);

    const onFileDownload = useCallback(async (fileName: string, activityId: string): Promise<any> => {
        const base64 = await clientActionsApi.getActivityFile(fileName, activityId);
        apiUtils.saveFileFromBase64(fileName, base64);
    }, []);

    const [preSessionMessageSending, setPreSessionMessageSending] = useState(false);
    const preSessionMessageSendingRef = useRef(false);
    const preSessionChatbotTyping = useMemo(() => {
        const data = (textChatActivity || avatarActivity)?.data as SessionActivityTextChatData;
        return !!data?.preSessionFlow?.flowSnapshot.modes?.chatbot && !!data.state?.[DefaultParticipant.Chatbot]?.typing;
    }, [textChatActivity, avatarActivity]);
    const preSessionChatbotBusy = useMemo(
        () => preSessionMessageSending || preSessionChatbotTyping,
        [preSessionMessageSending, preSessionChatbotTyping]
    );
    const onSubmitMessage = useCallback(async (message: Partial<TextChatMessage>): Promise<any> => {
        const activity = textChatActivity || avatarActivity;
        if (!activity) {
            return null;
        }
        if (!(activity.data as SessionActivityTextChatData)?.preSessionFlow?.flowSnapshot.modes?.chatbot) {
            return clientActionsApi.addActivityData(sessionParams, activity.id, {message});
        }
        if (preSessionMessageSendingRef.current || preSessionChatbotTyping) {
            return null;
        }
        preSessionMessageSendingRef.current = true;
        setPreSessionMessageSending(true);
        try {
            return await clientActionsApi.addActivityData(sessionParams, activity.id, {message});
        } finally {
            preSessionMessageSendingRef.current = false;
            setPreSessionMessageSending(false);
        }
    }, [avatarActivity, sessionParams, textChatActivity, preSessionChatbotTyping]);

    // Pinned on the activity at start, so it cannot change under a turn in progress.
    const voiceInputMode = useMemo(
        () => (textChatActivity?.data as SessionActivityTextChatData)?.streamAudio?.mode || VoiceInputMode.PushToTalk,
        [textChatActivity]
    );

    const isHandsFree = useMemo(() => voiceInputMode === VoiceInputMode.Toggle, [voiceInputMode]);

    // The chatbot answers spoken turns out loud — the replies arrive over the socket as finished audio.
    const isVoiceReply = useMemo(
        () => !!(textChatActivity?.data as SessionActivityTextChatData)?.streamAudio?.voiceReply,
        [textChatActivity]
    );

    // The bot's goodbye finished playing — tell the server so it can end the session right after.
    const onVoiceConversationEnd = useCallback(() => {
        if (session?.id) {
            socket.emit(SocketEvent.ActiveSessionActivityVoiceReplyEnded, {sessionId: session.id});
        }
    }, [session?.id, socket]);

    const {stop: stopVoiceReplyPlayback} = useVoiceReplyPlayback({
        socket, enabled: isVoiceReply && !!textChatActivity, onConversationEnd: onVoiceConversationEnd
    });

    // Voice input in the text chat. The microphone track is opened once and kept — stopping the turn stops the
    // recording, not the track. Re-opening it on every press cost hundreds of milliseconds (a permission
    // prompt on the first one), and the client is already speaking by then: that latency ate the first words,
    // which on a short phrase is most of it. Nothing is streamed while not recording.
    // Stops the microphone track and forgets it; the next startVoiceInput re-acquires it via getUserMedia.
    const releaseVoiceTrack = useCallback(() => {
        clearTimeout(voiceInputIdleRef.current);
        voiceTracksRef.current.forEach((track) => track.stop());
        voiceTracksRef.current = [];
        setVoiceTracks([]);
    }, []);

    const startVoiceInput = useCallback(async () => {
        // Pressing again during the trailing/idle windows cancels the pending stop/release and reuses the turn.
        clearTimeout(voiceInputReleaseRef.current);
        clearTimeout(voiceInputIdleRef.current);
        if (voiceRecording) {
            return;
        }
        try {
            const hasLiveTrack = voiceTracksRef.current.some((track) => track.readyState === 'live');
            if (!hasLiveTrack) {
                const stream = await navigator.mediaDevices.getUserMedia(VOICE_INPUT_CONSTRAINTS);
                voiceTracksRef.current = stream.getAudioTracks();
                setVoiceTracks(voiceTracksRef.current);
            }
            setVoiceRecording(true);
        } catch (error) {
            logger.debug(`Voice input: unable to open the microphone: ${error}`);
        }
    }, [voiceRecording]);

    const stopVoiceInput = useCallback(() => {
        clearTimeout(voiceInputTimeoutRef.current);
        clearTimeout(voiceInputReleaseRef.current);
        voiceInputReleaseRef.current = setTimeout(() => setVoiceRecording(false), VOICE_INPUT_RELEASE_DELAY);
    }, []);

    // Hands-free arming: opens the microphone (and asks for permission on the first time) and records
    // continuously, like the avatar — the streaming transcription provider draws the phrase boundaries itself,
    // so nothing chops the recording and the start of a phrase is never lost. Disarming drops the track.
    const armVoiceInput = useCallback(async () => {
        clearTimeout(voiceInputIdleRef.current);
        try {
            const hasLiveTrack = voiceTracksRef.current.some((track) => track.readyState === 'live');
            if (!hasLiveTrack) {
                const stream = await navigator.mediaDevices.getUserMedia(VOICE_INPUT_CONSTRAINTS);
                voiceTracksRef.current = stream.getAudioTracks();
                setVoiceTracks(voiceTracksRef.current);
            }
            setVoiceArmed(true);
            // This tap is the gesture that unlocks audio playback, so ask the server to replay the last bot
            // phrase now — the greeting the client has only seen as text so far. Once only, and only when the
            // chat actually speaks its replies.
            if (isVoiceReply && !voiceReplayRequestedRef.current && session?.id && localParticipant?.id && textChatActivity?.id) {
                voiceReplayRequestedRef.current = true;
                socket.emit(SocketEvent.ActiveSessionActivityRequestVoiceReply, {
                    sessionId: session.id, participantId: localParticipant.id, activityId: textChatActivity.id
                });
            }
        } catch (error) {
            logger.debug(`Voice input: unable to open the microphone: ${error}`);
        }
    }, [isVoiceReply, session?.id, localParticipant?.id, textChatActivity?.id, socket]);

    const disarmVoiceInput = useCallback(() => {
        setVoiceArmed(false);
        setVoiceRecording(false);
        releaseVoiceTrack();
    }, [releaseVoiceTrack]);

    // The chatbot is wrapping up: stop taking input right away so nothing new arrives while the goodbye plays and
    // the session winds down. The goodbye's own playback is unaffected — it lives in useVoiceReplyPlayback.
    useSocketEventCallback(socket, SocketEvent.ActiveSessionActivityChatbotEnding, useCallback(() => {
        setConversationEnding(true);
        disarmVoiceInput();
    }, [disarmVoiceInput]));

    // Barge-in only: recording runs continuously while armed, so speech detection no longer starts or stops a
    // turn — it just tells us the client began talking over the bot, which means stop the playback rather than
    // talk over them. Endpointing (where a phrase ends) belongs to the transcription provider now.
    const onSpeechStart = useCallback(() => stopVoiceReplyPlayback(), [stopVoiceReplyPlayback]);
    const onSpeechEnd = useCallback(() => undefined, []);

    useSpeechDetection({track: voiceTracks[0], enabled: voiceArmed, onSpeechStart, onSpeechEnd});

    // Push-to-talk only: a stuck button (lost mouseup, frozen tab) would otherwise keep the microphone open and
    // the speech service billing forever — so a held turn is capped and released on its own. Toggle mode has no
    // such failure mode (the microphone is switched off by an explicit click) and behaves like a call: it stays
    // on until the client stops it, the session ends, or a call takes over the audio.
    useEffect(() => {
        if (!voiceRecording || voiceInputMode === VoiceInputMode.Toggle) {
            return;
        }
        voiceInputTimeoutRef.current = setTimeout(() => {
            logger.debug('Voice input: maximum turn length reached, releasing the microphone');
            stopVoiceInput();
        }, MAX_VOICE_INPUT_DURATION);

        return () => clearTimeout(voiceInputTimeoutRef.current);
    }, [voiceRecording, voiceInputMode, stopVoiceInput]);

    // Not recording: hold the track briefly for the next turn, then release it so the mic indicator does not
    // stay lit. Recording again clears this in startVoiceInput.
    useEffect(() => {
        if (voiceRecording || voiceArmed || !voiceTracksRef.current.length) {
            return;
        }
        voiceInputIdleRef.current = setTimeout(releaseVoiceTrack, VOICE_INPUT_IDLE_RELEASE);
        return () => clearTimeout(voiceInputIdleRef.current);
    }, [voiceRecording, voiceArmed, releaseVoiceTrack]);

    // Release the microphone when the session ends: outside the widget the component is not unmounted, so the
    // track would otherwise stay open after the conversation is over.
    useEffect(() => {
        if (isStateChangedToFinished) {
            setVoiceArmed(false);
            setVoiceRecording(false);
            releaseVoiceTrack();
        }
    }, [isStateChangedToFinished, releaseVoiceTrack]);

    // Final safety net: stop the track when the component does unmount (the widget case).
    useEffect(() => releaseVoiceTrack, [releaseVoiceTrack]);

    // The server switches the microphone off after a long silence in toggle mode. Go through the same path as a
    // manual stop so the button, the track and the "Listening…" indicator all end up in their idle state — a
    // stream that died under a lit microphone would leave the client talking into nothing.
    const onVoiceInputStopped = useCallback(() => {
        setVoiceArmed(false);
        setVoiceRecording(false);
        releaseVoiceTrack();
    }, [releaseVoiceTrack]);

    useAudioStreamRecord({
        // Hands-free records for as long as the microphone is armed (the provider finds the phrase boundaries);
        // push-to-talk records only while the button is held.
        shouldRecord: !preSessionChatbotBusy && (isHandsFree ? voiceArmed : voiceRecording),
        socket,
        sessionId: session?.id,
        participantId: localParticipant?.id,
        activityId: textChatActivity?.id,
        tracks: voiceTracks,
        transcriptionLanguage,
        onStopped: onVoiceInputStopped
    });

    // A live audio/video/SIP activity runs its own continuous transcription over this socket, which has a single
    // STT client — holding the chat mic would fight it for the channel. Simplest robust rule: no chat voice
    // input while one of those is active (ended ones stay in the list, so check endDate).
    const hasLiveSpeechActivity = useMemo(
        () => [audioVideoActivity, audioActivity, sipActivity].some((activity) => activity && !activity.endDate),
        [audioVideoActivity, audioActivity, sipActivity]
    );

    // Gate on the TEXT-CHAT activity's own streamAudio, not the global audioStreamEnabled (which is an OR across
    // audio/video/avatar too), and only when no other speech modality holds the socket.
    const textChatStreamAudioEnabled = useMemo(
        () => !hasLiveSpeechActivity && !!audioStreamEnabled
            && !!(textChatActivity?.data as SessionActivityTextChatData)?.streamAudio?.enabled,
        [hasLiveSpeechActivity, audioStreamEnabled, textChatActivity]
    );

    // Voice input became unavailable (a call started, the activity changed) — possibly mid-recording, when the
    // button unmounts and its mouseup never fires. Stop and release so nothing stays live behind a hidden button.
    useEffect(() => {
        if (!textChatStreamAudioEnabled) {
            setVoiceArmed(false);
            setVoiceRecording(false);
            releaseVoiceTrack();
        }
    }, [textChatStreamAudioEnabled, releaseVoiceTrack]);

    const voiceInput = useMemo(() => ({
        enabled: textChatStreamAudioEnabled,
        // Hands-free shows the microphone as on for as long as it is listening, not only while a phrase is being
        // captured — the client switched it on and expects it to stay lit until they switch it off.
        recording: !preSessionChatbotBusy && (isHandsFree ? voiceArmed : voiceRecording),
        mode: voiceInputMode,
        onStart: isHandsFree ? armVoiceInput : startVoiceInput,
        onStop: isHandsFree ? disarmVoiceInput : stopVoiceInput
    }), [
        textChatStreamAudioEnabled, isHandsFree, voiceArmed, voiceRecording, voiceInputMode, preSessionChatbotBusy,
        armVoiceInput, disarmVoiceInput, startVoiceInput, stopVoiceInput
    ]);

    const onChangeTextChatActivityState = useCallback((state: SessionActivityTextChatDataState): Promise<any> => {
        if (!textChatActivity) {
            return null;
        }
        return clientActionsApi.addActivityData(sessionParams,
            textChatActivity.id,
            {state}
        );
    }, [sessionParams, textChatActivity]);

    const addFeedback = useCallback(async (feedback: SessionFeedback) => {
        await clientActionsApi.addFeedback(sessionParams, feedback, token);
        handleNotificationResult(clientNotifier.feedbackSent(feedback), token);
    }, [sessionParams, token, clientNotifier, handleNotificationResult]);

    const requestAppointment = useCallback(async (values: RequestAppointmentDetails) => {
        await clientActionsApi.requestAppointment(values, token);
        handleNotificationResult(clientNotifier.appointmentRequested(values), token);
    }, [token, clientNotifier, handleNotificationResult]);

    const leaveSession = useCallback(() => {
        if (!localSessionEnded &&
            (isCurrentStatus(SessionStatus.New) ||
            isCurrentStatus(SessionStatus.PreCall) ||
            isCurrentStatus(SessionStatus.Calling) ||
            isCurrentStatus(SessionStatus.InSession) ||
            isCurrentStatus(SessionStatus.OnHold))
        ) {
            clientActionsApi.leaveSession(sessionParams);
        }
    }, [isCurrentStatus, localSessionEnded, sessionParams]);

    useBeforeunload(leaveSession);

    const endLocalSession = useCallback(() => {
        leaveSession();
        setSessionEndedState();
    }, [leaveSession, setSessionEndedState]);

    const handleUploadSelfieVerificationResult = useCallback((isSuccess: boolean) =>
        updateParticipantAsyncRequest(
            AsyncRequestTypeName.UploadSelfieVerification,
            isSuccess ? AsyncRequestStatusName.Success : AsyncRequestStatusName.Failed
        ), [updateParticipantAsyncRequest]);

    const handleUploadSelfieVerificationScreenshotError = useCallback((error: Error) => {
        handleNotificationResult(clientNotifier.formatResult({
            name: NOTIFY_EVENT_NAMES.VALIDATE_SELFIE,
            success: false,
            ...(error && {
                error: {
                    message: error.message,
                    details: error.stack
                }
            })
        }));
        handleUploadSelfieVerificationResult(false);
    }, [clientNotifier, handleNotificationResult, handleUploadSelfieVerificationResult]);

    const handleUploadSelfieVerificationScreenshot = useCallback((base64: string) => {
        const result = clientNotifier.validateSelfie(base64);
        handleNotificationResult(result);

        if (!result.success) {
            if (isDevelopmentMode && sessionUtils.isClientDeviceSDK(device)) {
                setTimeout(() => {
                    handleUploadSelfieVerificationResult(true);
                }, 5000);
            } else {
                handleUploadSelfieVerificationResult(false);
            }
        }
    }, [device, handleNotificationResult, handleUploadSelfieVerificationResult, clientNotifier]);

    const executeUploadSelfieVerification = useCallback(() => {
        getScreenshotBase64(SessionScreenshotSubject.LocalVideo)
            .then(handleUploadSelfieVerificationScreenshot)
            .catch(handleUploadSelfieVerificationScreenshotError);
    }, [handleUploadSelfieVerificationScreenshotError, handleUploadSelfieVerificationScreenshot]);

    const handleStorageChange = useCallback((event) => {
        if (event.storageArea === storage) {
            // a case when a page was opened in a new tab
            // if an old is opened it will clean a token in a local storage
            // and refresh and open page in a new tab will not work anymore
            if (!isCurrentStatus(SessionStatus.SessionEnded) && token && InviteeToken.get() !== token) {
                logger.debug('Refresh client token in a storage');
                InviteeToken.set(token);
            }
        }
    }, [isCurrentStatus, token]);

    const handleReassignedSession = useCallback(() => {
        resetSessionValues();
        startReassignedSession();
    }, [resetSessionValues, startReassignedSession]);

    const addUntreatedAsyncRequest = useCallback((asyncRequest: ParticipantAsyncRequest<AsyncRequestData>) => {
        logger.debug('Found untreated async request: ', asyncRequest.type);
        setUntreatedAsyncRequests((prev) => [...prev, asyncRequest]);
    }, []);

    const handleSessionUpdate = useCallback(() => {
        logger.debug(`Handle session update. Prev version:, ${prevSessionVersion}, new version: ${session?.version}`);

        if (!isPreviousStatus(sessionStatus)) {
            statusChanged(session);
        }

        if (isCurrentStatus(SessionStatus.SessionEnded)) {
            if (session.reassigned && isClient) {
                handleReassignedSession();
                return;
            } else {
                setSessionEndedState();
            }
        } else if (
            isCurrentStatus(SessionStatus.InSession) ||
            // Pre-session-flow runs while status is still PreCall/Chatbot. Async-request steps
            // (Geolocation, SumaDocumentVerificationSDK) create participant async-requests that
            // need the client to be picked up here — otherwise the request sits at `Requested`
            // and the browser permission UI never fires.
            ((isCurrentStatus(SessionStatus.PreCall) || isCurrentStatus(SessionStatus.Chatbot)) &&
                !!((textChatActivity?.data as SessionActivityTextChatData)?.preSessionFlow ||
                    (avatarActivity?.data as SessionActivityTextChatData)?.preSessionFlow))
        ) {
            forEach(session.localParticipant.asyncRequests, (asyncRequest: ParticipantAsyncRequest) => {
                if (asyncRequest.status?.name === AsyncRequestStatusName.Requested) {
                    const prevRequest = find(prevSession?.localParticipant?.asyncRequests, {type: asyncRequest.type});
                    if (prevRequest?.status?.name !== AsyncRequestStatusName.Requested) {
                        logger.debug('Update async request status to in progress: ', {type: asyncRequest.type});
                        updateParticipantAsyncRequest(asyncRequest.type, AsyncRequestStatusName.InProgress)
                            .then(() => {
                                addUntreatedAsyncRequest(asyncRequest);
                            });
                    }
                }
            });
        }

        const textChatActivityData = textChatActivity?.data as SessionActivityTextChatData;
        if (textChatActivity && !prevTextChatActivity && !textChatThreads) {
            setTextChatThreads(textChatActivityData?.threads);
        } else if (!textChatActivity && prevTextChatActivity && textChatThreads) {
            setTextChatThreads(null);
        }

        if (textChatActivity) {
            setTextChatInteractiveChatState(textChatActivityData?.interactiveChatState);
        }

        const avatarActivityData = avatarActivity?.data as SessionActivityTextChatData;
        if (avatarActivity && !prevAvatarActivity && !avatarThreads) {
            setAvatarThreads(avatarActivityData?.threads);
        } else if (!avatarActivity && prevAvatarActivity && avatarThreads) {
            setAvatarThreads(null);
        }

        // used on server restart as server cleans all participant socket ids
        if (!isOpenedInNewTab(session) && !isSessionEnded(session) &&
            socket.connected && session?.localParticipant?.socket?.id !== socket.id) {
            refreshAuth();
        }

        if (isOpenedInNewTab(session)) {
            setSessionEndedState();
        }
    }, [
        prevSessionVersion, session, isPreviousStatus, sessionStatus, isCurrentStatus, textChatActivity, isClient,
        prevTextChatActivity, textChatThreads, avatarActivity, prevAvatarActivity, avatarThreads, isOpenedInNewTab,
        isSessionEnded, socket.connected, socket.id, statusChanged, handleReassignedSession, setSessionEndedState,
        prevSession?.localParticipant?.asyncRequests, updateParticipantAsyncRequest, addUntreatedAsyncRequest, refreshAuth
    ]);

    const handleGeolocationResult = useCallback((
        status: AsyncRequestStatusName,
        data: {latitude: number, longitude: number, accuracy: number} | null,
        errorCode = null
    ) => {
        if (errorCode) {
            const message = errorCode === GEOLOCATION_ERROR_CODE.NotSupported ?
                'NOT_SUPPORTED' : errorCode === GEOLOCATION_ERROR_CODE.NotAllowed ?
                    'NOT_ALLOWED' : 'UNEXPECTED';
            notifier.error({details: t(GEOLOCATION_ERROR_T_KEYS[message])});
        }

        return updateParticipantAsyncRequest(AsyncRequestTypeName.Geolocation, status, {data});
    }, [t, updateParticipantAsyncRequest]);

    const shareGeolocation = useCallback(() => {
        if (!navigator.geolocation) {
            handleGeolocationResult(AsyncRequestStatusName.Failed, null, GEOLOCATION_ERROR_CODE.NotSupported);
        } else {
            navigator.geolocation.getCurrentPosition((position) => {
                const {latitude, longitude, accuracy} = position.coords;
                handleGeolocationResult(AsyncRequestStatusName.Success, {latitude, longitude, accuracy});
            }, (error) => handleGeolocationResult(AsyncRequestStatusName.Failed, null, error.code), {
                enableHighAccuracy: true,
                timeout: 30000
            });
        }
    }, [handleGeolocationResult]);

    const handleParticipantAsyncRequest = useCallback((asyncRequest: ParticipantAsyncRequest) => {
        if (asyncRequest.type === AsyncRequestTypeName.Geolocation) {
            shareGeolocation();
        } else if (asyncRequest.type === AsyncRequestTypeName.UploadSelfieVerification) {
            executeUploadSelfieVerification();
        }
    }, [shareGeolocation, executeUploadSelfieVerification]);

    const onTogglePictureInPicture = useCallback(() => {
        if (!pictureInPictureStore.getValue().active) {
            const containerSelector = audioVideoViewConference ? sessionUtils.getParticipantVideoSelector(
                agent.id
            ) : `#${REMOTE_MEDIA_ID}`;
            pictureInPictureStore.mergeValue({container: containerSelector, enabled: true});
        } else {
            pictureInPictureStore.mergeValue({container: null, enabled: false});
        }
    }, [agent, audioVideoViewConference]);

    const handleVideoPinParticipantUpdate = useCallback((_videoPinParticipant: VideoPinParticipant, participantId?: string) => {
        if (participantId) {
            if (pinnedParticipantId === participantId) {
                setPinnedParticipantId(null);
            } else {
                setPinnedParticipantId(participantId);
            }
        } else {
            if (inSessionDataStore.getValue().videoPinParticipant !== _videoPinParticipant) {
                inSessionDataStore.mergeValue({videoPinParticipant: _videoPinParticipant});
                setVideoPinParticipant(_videoPinParticipant);
            } else {
                inSessionDataStore.mergeValue({videoPinParticipant: null});
                setVideoPinParticipant(null);
            }
        }
    }, [pinnedParticipantId]);

    useEffect(() => {
        if (!isPayloadChanged || !session || sessionVersion === prevSessionVersion) {
            return;
        }

        handleSessionUpdate();
    }, [handleSessionUpdate, isPayloadChanged, sessionVersion, prevSessionVersion, session]);

    useEffect(() => {
        if (audioEnabledChanged && changedBy && changedBy !== localParticipant.id && !isWebviewVideoOnly) {
            const title = audioEnabled ? t(T_KEYS.AUDIO_ENABLED) : t(T_KEYS.AUDIO_DISABLED);
            notifier.info({title});
        }
    }, [device, audioEnabledChanged, changedBy, audioEnabled, localParticipant, isWebviewVideoOnly, t]);

    useEffect(() => {
        if (videoEnabledChanged && changedBy && changedBy !== localParticipant.id && !isWebviewVideoOnly) {
            const title = videoEnabled ? t(T_KEYS.VIDEO_ENABLED) : t(T_KEYS.VIDEO_DISABLED);
            notifier.info({title});
        }
    }, [device, videoEnabledChanged, changedBy, videoEnabled, localParticipant, isWebviewVideoOnly, t]);

    useEffect(() => {
        window.addEventListener('storage', handleStorageChange, false);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [handleStorageChange]);

    useEffect(() => {
        if (errorMessage && !localSessionEnded) {
            setSessionEndedState();
        }
    }, [errorMessage, localSessionEnded, setSessionEndedState]);

    useEffect(() => {
        if (isStateChangedToFinished) {
            notifyAboutEndSession(session?.result, InviteeToken.get());
            InviteeToken.remove();
        }
    }, [isStateChangedToFinished, notifyAboutEndSession, session]);

    useEffect(() => {
        forEach(untreatedAsyncRequests, (request) => {
            handleParticipantAsyncRequest(request);
            setTreatedAsyncRequest(request.type);
        });
    }, [untreatedAsyncRequests, setTreatedAsyncRequest, handleParticipantAsyncRequest]);

    return (
        <ClientContext.Provider value={{
            socket,
            ...payload,
            ...contextProps,
            audioEnabled,
            videoEnabled,
            videoPinParticipant,
            fullScreenVideo,
            currentView,
            isDevelopmentMode,
            videoDevices,
            audioDevices,
            videoConstraints,
            textChatThreads,
            textChatInteractiveChatState,
            avatarThreads,
            participantsNetworkQuality,
            untreatedAsyncRequests,
            twilioDevice,
            tracks,
            voiceInput,
            textChatInputDisabled: conversationEnding || preSessionChatbotBusy,
            preSessionFormDrafts,
            pinnedParticipantId,
            handleVideoPinParticipantUpdate,
            onToggleAudio,
            onToggleVideo,
            setScreenSharingActivityStatus,
            setVideoConstraints,
            onToggleFullScreenVideo,
            onLocalNetworkQualityLevelChange,
            onRemoteNetworkQualityLevelChange,
            setMediaDevices,
            notifyWebRTCStartSuccess,
            notifyWebRTCStartFailed,
            endLocalSession,
            handleUploadSelfieVerificationResult,
            executeUploadSelfieVerification,
            addFeedback,
            requestAppointment,
            updateParticipantAsyncRequest,
            onFileDownload,
            onSubmitMessage,
            onChangeTextChatActivityState,
            setTreatedAsyncRequest,
            onTogglePictureInPicture,
            setTwilioDevice,
            initTwilioDevice,
            setTracks
        }}
        >
            {children}
            <canvas id={SCREENSHOT_CANVAS_ID} className='tw-hidden' />
            <PictureInPictureObserver />
        </ClientContext.Provider>
    );
};
